
import { defineComponent, inject, onMounted, onUnmounted, PropType, provide, ref, watchEffect } from 'vue';
import { useVModel } from 'vue-composable';
import { useOnOutsidePress } from '@/utils';
import { ButtonTypes } from '@/types';
import { InjectionKeys } from '@/constants';
import MaterialIcon from '@/components/MaterialIcon.vue';
import ButtonInputStyleProvider from '@/components/style-providers/ButtonInputStyleProvider.vue';
import ButtonInput from '@/components/form-controls/base/ButtonInput.vue';

export default defineComponent({
  name: 'AppModal',
  emits: ['update:openState', 'update:dirtyState', 'submit'],
  components: {
    MaterialIcon,
    ButtonInputStyleProvider,
    ButtonInput
  },
  props: {
    submitText: {
      type: String,
      default: 'Submit'
    },
    cancelText: {
      type: String,
      default: 'Cancel'
    },
    openState: {
      type: Boolean,
      required: true
    },
    doDirtyCheck: {
      type: Boolean,
      required: false,
      default: true
    },
    ignoredQueries: {
      type: Array as PropType<Array<string>>,
      required: false,
      default: () => ['.pac-container', '.pac-container *', '.pac-item', '.pac-item *']
    },
    submitButtonType: {
      type: String as PropType<ButtonTypes>,
      required: false,
      default: 'primary'
    },
    centerBody: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  setup(props, { emit }) {
    const isOpen = useVModel(props, 'openState');

    watchEffect(() => {
      document.getElementsByTagName('body')[0].style.overflowY = isOpen.value ? 'hidden' : 'auto';
    });

    const isDirty = ref(false);
    const setDirty = () => {
      isDirty.value = true;
    };

    const close = () => {
      isDirty.value = false;
      isOpen.value = false;
    };

    provide(InjectionKeys.SetFormDirty, setDirty);
    const formSubmit = inject(InjectionKeys.FormSubmit);
    const formCancel = inject(InjectionKeys.FormCancel);

    const submit = async (e: Event) => {
      e.preventDefault();
      const resetForm = await formSubmit?.();
      emit('submit');
      resetForm && resetForm?.();
      close();
    };

    const cancel = () => {
      formCancel?.();
      close();
    };

    const appModalRef = ref<HTMLDivElement>();
    const destroyOnOutsidePressListener = ref();

    onMounted(() => {
      destroyOnOutsidePressListener.value = useOnOutsidePress(appModalRef, cancel, props.ignoredQueries);
    });

    onUnmounted(() => {
      destroyOnOutsidePressListener.value();
    });

    return {
      isOpen,
      isDirty,
      setDirty,
      submit,
      cancel,
      appModalRef
    };
  }
});
